"use client";

import { useRouter } from "next/navigation";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { getOAuthLoginUrl, getOAuthLogoutUrl } from "@/lib/oauth";
import {
  clearAllTokens,
  getAccessToken,
  isTokenValid,
  setAccessToken as setAccessTokenPersistent,
  setIdToken as setIdTokenPersistent,
  setRefreshToken as setRefreshTokenPersistent,
} from "@/lib/tokenManager";

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  accessToken: string | null;
  login: () => void;
  logout: () => void;
  logoutLocal: () => void;
  logoutGlobal: () => void;
  refreshToken: () => Promise<void>;
  setOAuthTokens: (tokens: { accessToken: string; refreshToken?: string; idToken?: string }) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [accessToken, setAccessTokenState] = useState<string | null>(() => getAccessToken());
  const router = useRouter();

  const checkAuth = useCallback(async () => {
    // TEMPORARY: Bypass auth in development - COMMENTED OUT FOR TESTING
    // if (process.env.NODE_ENV === "development") {
    //   setIsAuthenticated(true);
    //   setAccessTokenState("dev-bypass-token");
    //   setIsLoading(false);
    //   return;
    // }

    try {
      // CRITICAL: Skip auth check on OAuth callback pages to prevent race condition
      // The callback page handles token exchange and will redirect after completion
      if (typeof window !== "undefined") {
        const path = window.location.pathname;
        if (path === "/auth/callback" || path === "/auth/switch-callback") {
          console.log("[AuthProvider] Skipping auth check on OAuth callback page:", path);
          setIsLoading(false);
          return;
        }
      }

      // Check if we have a valid token in storage
      const token = getAccessToken();
      if (token && isTokenValid(token)) {
        setAccessTokenState(token);
        setIsAuthenticated(true);
        setIsLoading(false);
        return;
      }

      // If no valid token, check session API
      const response = await fetch("/api/auth/session", {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        if (data.accessToken) {
          setAccessTokenPersistent(data.accessToken);
          setAccessTokenState(data.accessToken);
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
          setAccessTokenState(null);
        }
      } else {
        setIsAuthenticated(false);
        setAccessTokenState(null);
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      setIsAuthenticated(false);
      setAccessTokenState(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(() => {
    window.location.href = getOAuthLoginUrl();
  }, []);

  const logoutLocal = useCallback(async () => {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      clearAllTokens();
      setIsAuthenticated(false);
      setAccessTokenState(null);

      // Clear Apollo cache and persisted cache from localStorage
      if (typeof window !== "undefined") {
        try {
          localStorage.removeItem("apollo-cache-persist");
        } catch (error) {
          console.error("Failed to clear Apollo cache from localStorage:", error);
        }
      }

      router.push("/signin");
    }
  }, [router]);

  const logoutGlobal = useCallback(async () => {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
      clearAllTokens();
      setIsAuthenticated(false);
      setAccessTokenState(null);

      // Clear Apollo cache and persisted cache from localStorage
      if (typeof window !== "undefined") {
        try {
          localStorage.removeItem("apollo-cache-persist");
        } catch (error) {
          console.error("Failed to clear Apollo cache from localStorage:", error);
        }

        window.location.href = getOAuthLogoutUrl();
      } else {
        router.push("/signin");
      }
    } catch (error) {
      console.error("Global logout failed:", error);

      // Still try to clear cache even if logout API fails
      if (typeof window !== "undefined") {
        try {
          localStorage.removeItem("apollo-cache-persist");
        } catch (err) {
          console.error("Failed to clear Apollo cache from localStorage:", err);
        }
      }

      router.push("/signin");
    }
  }, [router]);

  const logout = logoutLocal;

  const refreshToken = useCallback(async () => {
    try {
      const response = await fetch("/api/auth/refresh", {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        if (data.accessToken) {
          setAccessTokenPersistent(data.accessToken);
          setAccessTokenState(data.accessToken);
        }
        if (data.refreshToken) {
          setRefreshTokenPersistent(data.refreshToken);
        }
        if (data.idToken) {
          setIdTokenPersistent(data.idToken);
        }
        setIsAuthenticated(true);
      } else {
        clearAllTokens();
        setIsAuthenticated(false);
        setAccessTokenState(null);
      }
    } catch (error) {
      console.error("Token refresh failed:", error);
      clearAllTokens();
      setIsAuthenticated(false);
      setAccessTokenState(null);
    }
  }, []);

  const setOAuthTokens = useCallback(
    async (tokens: { accessToken: string; refreshToken?: string; idToken?: string }) => {
      // Store tokens in both localStorage and cookies for middleware access
      setAccessTokenPersistent(tokens.accessToken);
      setAccessTokenState(tokens.accessToken);

      if (tokens.refreshToken) {
        setRefreshTokenPersistent(tokens.refreshToken);
      }

      if (tokens.idToken) {
        setIdTokenPersistent(tokens.idToken);
      }

      setIsAuthenticated(true);
    },
    []
  );

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        accessToken,
        login,
        logout,
        logoutLocal,
        logoutGlobal,
        refreshToken,
        setOAuthTokens,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
